import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect } from "react";

/**
 * Modal — bulletproof layout.
 *
 *   Desktop (md+): top-anchored at 1.5rem from viewport top, horizontally
 *                  centred. Capped to `calc(100dvh - 3rem)` so header + X
 *                  button stay visible even when content is tall. (Vertical
 *                  centring pushed the top off-screen on some window sizes —
 *                  especially inside the Electron desktop shell.)
 *   Mobile: bottom-sheet anchored to `inset-x-0 bottom-0`, capped to 100dvh.
 *
 * Internal layout:
 *   ┌─ header  (shrink-0, always visible — X button never clipped)
 *   ├─ body    (flex-1, overflow-y-auto, min-h-0)   ← the only scroll surface
 *   └─ footer  (shrink-0, optional)
 *
 * Flex-based centring proved unreliable in some Chromium builds when the
 * modal grew tall; fixed + top-anchored avoids that entire class of bugs.
 */
export default function Modal({ open, onClose, title, children, footer, testId = "modal", maxWidth = "max-w-lg" }) {
  // Wave 26.10.4 — body-scroll-lock so background doesn't scroll when modal
  // is open on mobile. Uses position:fixed approach to prevent iOS Safari
  // rubber-band scrolling. Keeps a stack counter so nested modals work.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    const stack = (Number(document.body.dataset.hvModalStack) || 0) + 1;
    document.body.dataset.hvModalStack = String(stack);
    if (stack === 1) {
      // Only the first opening modal should pin the body.
      document.body.dataset.hvModalScrollY = String(scrollY);
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    }
    return () => {
      const remaining = (Number(document.body.dataset.hvModalStack) || 1) - 1;
      if (remaining <= 0) {
        const savedY = Number(document.body.dataset.hvModalScrollY) || 0;
        document.body.style.overflow = prev.overflow;
        document.body.style.position = prev.position;
        document.body.style.top = prev.top;
        document.body.style.width = prev.width;
        delete document.body.dataset.hvModalStack;
        delete document.body.dataset.hvModalScrollY;
        // Restore scroll position so user lands where they were.
        window.scrollTo(0, savedY);
      } else {
        document.body.dataset.hvModalStack = String(remaining);
      }
    };
  }, [open]);

  if (!open) return null;
  const mdMaxWidthClass = MD_MAX_WIDTH[maxWidth] || "md:max-w-lg";
  // IMPORTANT — portal to document.body. Otherwise an ancestor with `transform`
  // (e.g. CSS animations, `will-change`) becomes the containing block for our
  // `position: fixed` children, which breaks viewport-relative centring.
  return createPortal((
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm hv-safe-top hv-safe-bottom hv-safe-x"
      data-testid={testId}
      data-hv-modal-portal="true"
      onClick={onClose}
    >
      <div
        className={`fixed bg-[#121419] border border-white/10 flex flex-col inset-x-0 bottom-0 rounded-t-2xl max-h-[calc(100dvh-env(safe-area-inset-top))] md:inset-x-auto md:bottom-auto md:top-[max(4rem,env(safe-area-inset-top))] md:left-1/2 md:-translate-x-1/2 md:w-[calc(100vw-3rem)] md:rounded-lg md:max-h-[calc(100dvh-5rem)] ${mdMaxWidthClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
          <h3 className="font-display text-lg font-bold tracking-tight pr-2 min-w-0 truncate">{title}</h3>
          <button
            onClick={onClose}
            data-testid={`${testId}-close`}
            className="text-[#647087] hover:text-white transition shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-5 flex-1 min-h-0" data-testid={`${testId}-body`}>
          {children}
        </div>
        {footer && (
          <div
            className="px-5 py-3 border-t border-white/10 bg-[#121419] shrink-0"
            data-testid={`${testId}-footer`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  ), document.body);
}

/** Tailwind can't interpolate dynamic class names, so map explicit values.
 *  Callers pass `max-w-lg` etc. (the mobile-friendly shorthand) and we apply
 *  the `md:` variant so the cap only kicks in on desktop (mobile stays full
 *  width as a bottom-sheet). The string comment below keeps Tailwind JIT
 *  aware of these classes — do not remove.
 *    md:max-w-sm md:max-w-md md:max-w-lg md:max-w-xl md:max-w-2xl md:max-w-3xl md:max-w-4xl
 *    md:max-w-5xl md:max-w-6xl md:max-w-7xl
 */
const MD_MAX_WIDTH = {
  "max-w-sm": "md:max-w-sm",
  "max-w-md": "md:max-w-md",
  "max-w-lg": "md:max-w-lg",
  "max-w-xl": "md:max-w-xl",
  "max-w-2xl": "md:max-w-2xl",
  "max-w-3xl": "md:max-w-3xl",
  "max-w-4xl": "md:max-w-4xl",
  "max-w-5xl": "md:max-w-5xl",
  "max-w-6xl": "md:max-w-6xl",
  "max-w-7xl": "md:max-w-7xl",
};
